import type { ProblemDetail } from '@shared/types';

export class ApiError extends Error {
  status: number;
  detail?: ProblemDetail;

  constructor(status: number, message: string, detail?: ProblemDetail) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

interface ApiOptions extends RequestInit {
  json?: unknown;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { json, headers, ...rest } = opts;

  const init: RequestInit = {
    credentials: 'same-origin',
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
  };
  if (json !== undefined) init.body = JSON.stringify(json);

  const res = await fetch(path, init);

  // Empty body
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json') || contentType.includes('application/problem+json');

  if (!res.ok) {
    let problem: ProblemDetail | undefined;
    if (isJson) {
      try {
        problem = (await res.json()) as ProblemDetail;
      } catch {
        // ignore
      }
    }
    throw new ApiError(res.status, problem?.title ?? `HTTP ${res.status}`, problem);
  }

  if (!isJson) return (await res.text()) as T;
  return (await res.json()) as T;
}

/** GET-Helper für TanStack Query queryFn. */
export function apiGet<T>(path: string, opts?: ApiOptions): () => Promise<T> {
  return () => api<T>(path, opts);
}
